import React, { useState } from "react";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, FileText, Download, Loader2, CheckCircle2, X } from "lucide-react";

const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DOC_TYPES = [
  { key: "test-certificate", label: "Test Certificate", testid: "dl-tc" },
  { key: "packing-list", label: "Packing List", testid: "dl-pl" },
  { key: "packing-stickers", label: "Packing Stickers (A4)", testid: "dl-stickers" },
  { key: "pyro-stickers", label: "Pyro Sleeve Stickers (40×20mm)", testid: "dl-pyro" },
];

function FilePicker({ file, setFile, label, testid }) {
  return (
    <label className="block group cursor-pointer" data-testid={testid}>
      <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">{label}</div>
      <div className={`border-2 border-dashed rounded-md p-6 text-center transition-colors ${file ? "border-emerald-400 bg-emerald-50" : "border-gray-300 hover:border-slate-500 bg-white"}`}>
        <input type="file" accept="application/pdf" className="hidden"
          onChange={(e) => setFile(e.target.files[0] || null)}
          data-testid={`${testid}-input`} />
        {file ? (
          <div className="flex items-center justify-center gap-2 text-emerald-700">
            <CheckCircle2 size={18} /> <span className="text-sm truncate max-w-[240px]">{file.name}</span>
            <button type="button" onClick={(e) => { e.preventDefault(); setFile(null); }}
              className="text-slate-400 hover:text-red-500" data-testid={`${testid}-clear`}>
              <X size={16} />
            </button>
          </div>
        ) : (
          <div className="text-slate-500">
            <Upload size={22} className="mx-auto mb-2" />
            <div className="text-sm">Click to select PDF</div>
          </div>
        )}
      </div>
    </label>
  );
}

export default function TataDispatch() {
  const [invoice, setInvoice] = useState(null);
  const [ecci, setEcci] = useState(null);
  const [loading, setLoading] = useState(false);
  const [doc, setDoc] = useState(null);

  const extract = async () => {
    if (!invoice || !ecci) { toast.error("Please select both invoice and ECCI PDFs"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("invoice", invoice);
      fd.append("ecci", ecci);
      const { data } = await api.post("/tata/extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDoc(data);
      toast.success(`Extracted ${data.line_items.length} line items`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Extraction failed");
    } finally { setLoading(false); }
  };

  const updateLine = (idx, patch) => {
    setDoc((d) => {
      const items = d.line_items.map((li, i) => i === idx ? { ...li, ...patch } : li);
      return { ...d, line_items: items };
    });
  };

  const persistThenDownload = async (docType) => {
    try {
      await api.patch(`/tata/docs/${doc.id}`, { line_items: doc.line_items, test_date: doc.test_date, test_certificate_no: doc.test_certificate_no });
      const url = `${REACT_APP_BACKEND_URL}/api/tata/docs/${doc.id}/${docType}.xlsx`;
      // Use a real navigation with credentials so cookie auth flows through
      const resp = await api.get(`/tata/docs/${doc.id}/${docType}.xlsx`, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      const cd = resp.headers["content-disposition"] || "";
      const m = cd.match(/filename="([^"]+)"/);
      a.download = m ? m[1] : `${docType}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(a.href);
      toast.success(`${docType}.xlsx downloaded`);
    } catch (e) {
      toast.error(`Download failed: ${e?.response?.status || ""}`);
    }
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="tata-dispatch-page">
        <header>
          <h1 className="font-display text-2xl font-bold text-slate-900">Tata Steel — Dispatch Documents</h1>
          <p className="text-sm text-slate-500 mt-1">Upload your Tally invoice + Tata ECCI PDF. System auto-generates Test Certificate, Packing List, Packing Stickers, and Pyro Sleeve Stickers.</p>
        </header>

        {!doc && (
          <div className="bg-white rounded-md border border-gray-200 p-6" data-testid="upload-panel">
            <div className="grid md:grid-cols-2 gap-6">
              <FilePicker file={invoice} setFile={setInvoice} label="Sales Invoice (Tally PDF)" testid="picker-invoice" />
              <FilePicker file={ecci} setFile={setEcci} label="Tata ECCI (Electronic Challan Cum Invoice)" testid="picker-ecci" />
            </div>
            <div className="mt-6 flex justify-end">
              <button
                onClick={extract}
                disabled={loading || !invoice || !ecci}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-slate-900 text-white rounded-md text-sm font-medium hover:bg-slate-800 disabled:opacity-50 transition-colors"
                data-testid="extract-btn"
              >
                {loading ? <><Loader2 size={16} className="animate-spin" /> Extracting…</> : <><FileText size={16} /> Extract & Prepare Documents</>}
              </button>
            </div>
          </div>
        )}

        {doc && (
          <>
            <div className="bg-white rounded-md border border-gray-200 p-6" data-testid="doc-summary">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <Kv label="Invoice No" value={doc.invoice?.invoice_no} />
                  <Kv label="Invoice Date" value={doc.invoice?.invoice_date} />
                  <Kv label="ECCI No" value={doc.ecci?.ecci_no} />
                  <Kv label="ECCI Date" value={doc.ecci?.ecci_date} />
                  <Kv label="Order No" value={doc.ecci?.order_no} />
                  <Kv label="PO Ref" value={doc.invoice?.po_ref} />
                  <Kv label="Warehouse" value={doc.ecci?.warehouse} />
                  <Kv label="Vehicle No" value={doc.ecci?.vehicle_no} />
                </div>
                <button onClick={() => setDoc(null)} className="text-sm text-slate-500 hover:text-slate-900 underline" data-testid="reset-btn">Start over</button>
              </div>
              <div className="mt-4 grid sm:grid-cols-2 gap-4">
                <Field label="Test Certificate No" value={doc.test_certificate_no} onChange={(v) => setDoc({ ...doc, test_certificate_no: v })} testid="tc-no" />
                <Field label="Test Date" value={doc.test_date} onChange={(v) => setDoc({ ...doc, test_date: v })} testid="test-date" />
              </div>
            </div>

            <div className="bg-white rounded-md border border-gray-200 overflow-x-auto" data-testid="line-items-table">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-3 py-2 text-left">#</th>
                    <th className="px-3 py-2 text-left">Material No</th>
                    <th className="px-3 py-2 text-left">Hose No</th>
                    <th className="px-3 py-2 text-left">Qty</th>
                    <th className="px-3 py-2 text-left">WP (bar)</th>
                    <th className="px-3 py-2 text-left">Test P (bar)</th>
                    <th className="px-3 py-2 text-left">Duration (s)</th>
                    <th className="px-3 py-2 text-left">Leakage</th>
                    <th className="px-3 py-2 text-left"># Packets</th>
                    <th className="px-3 py-2 text-left">Pyro?</th>
                  </tr>
                </thead>
                <tbody>
                  {doc.line_items.map((li, i) => (
                    <tr key={li.hose_no || `${li.material_no}-${li.serial_no}`} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`row-${i}`}>
                      <td className="px-3 py-2 font-mono">{li.serial_no}</td>
                      <td className="px-3 py-2 font-mono">{li.material_no}</td>
                      <td className="px-3 py-2 font-mono text-xs">{li.hose_no}</td>
                      <td className="px-3 py-2">{li.quantity} {li.unit}</td>
                      <td className="px-3 py-2 font-mono">{li.working_pressure_bar}</td>
                      <td className="px-3 py-2"><input type="number" value={li.test_pressure_bar} onChange={(e) => updateLine(i, { test_pressure_bar: parseFloat(e.target.value) || 0 })} className={cellCls} data-testid={`test-p-${i}`} /></td>
                      <td className="px-3 py-2"><input type="number" value={li.duration_seconds} onChange={(e) => updateLine(i, { duration_seconds: parseInt(e.target.value) || 0 })} className={cellCls} data-testid={`dur-${i}`} /></td>
                      <td className="px-3 py-2">
                        <select value={li.leakage} onChange={(e) => updateLine(i, { leakage: e.target.value })} className={cellCls} data-testid={`leak-${i}`}>
                          <option>NO</option><option>YES</option>
                        </select>
                      </td>
                      <td className="px-3 py-2"><input type="number" min="1" value={li.packing_qty} onChange={(e) => updateLine(i, { packing_qty: parseInt(e.target.value) || 1 })} className={cellCls} data-testid={`pack-${i}`} /></td>
                      <td className="px-3 py-2 text-center">
                        <input type="checkbox" checked={!!li.is_pyro} onChange={(e) => updateLine(i, { is_pyro: e.target.checked })} className="w-4 h-4" data-testid={`pyro-${i}`} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="bg-white rounded-md border border-gray-200 p-6" data-testid="download-panel">
              <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-4">Downloads · XLSX</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {DOC_TYPES.map((d) => (
                  <button
                    key={d.key}
                    onClick={() => persistThenDownload(d.key)}
                    className="inline-flex items-center justify-center gap-2 px-4 py-3 border border-slate-300 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-900 hover:text-white hover:border-slate-900 transition-colors"
                    data-testid={d.testid}
                  >
                    <Download size={16} /> {d.label}
                  </button>
                ))}
              </div>
              <p className="mt-4 text-xs text-slate-400">Tick the "Pyro?" checkbox on any line to include it in the Pyro Sleeve Sticker sheet.</p>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}

const cellCls = "w-24 px-2 py-1 border border-gray-200 rounded text-sm";

function Kv({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">{label}</div>
      <div className="text-slate-900 font-medium mt-0.5">{value || "-"}</div>
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-1">{label}</div>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" data-testid={testid} />
    </label>
  );
}
